import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { useMaintenance } from "../context/MaintenanceContext";
import { useAuth } from "../context/AuthContext";

/**
 * Countdown banner shown to signed-in creators + admins when a maintenance
 * window is scheduled within the next 24 hours (and maintenance isn't yet ON).
 * Hidden when the window is more than 24h away, already active, or absent.
 */
export default function ScheduledMaintenanceBanner() {
  const { user } = useAuth();
  const { enabled, start_at: startAt, end_at: endAt } = useMaintenance();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // 30s tick — matches the maintenance polling cadence.
    const iv = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(iv);
  }, []);

  if (enabled || !startAt || !user) return null;
  if (!(user.role === "creator" || user.role === "admin")) return null;

  const startMs = Date.parse(startAt);
  if (Number.isNaN(startMs)) return null;
  const msUntil = startMs - now;
  if (msUntil <= 0) return null;                 // window already started
  if (msUntil > 24 * 3600 * 1000) return null;   // >24h away — stay quiet

  const totalMin = Math.floor(msUntil / 60000);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  const countdown = hh > 0 ? `${hh}h ${String(mm).padStart(2, "0")}m` : `${mm}m`;

  const local = new Date(startMs).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const endLocal = endAt
    ? new Date(Date.parse(endAt)).toLocaleString(undefined, {
        hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <div
      className="bg-[#EAB308]/15 border-y border-[#EAB308]/30 text-[#FDE68A] px-4 py-2.5 text-xs sm:text-sm flex items-center justify-center gap-3"
      data-testid="scheduled-maintenance-banner"
    >
      <CalendarClock className="h-4 w-4 shrink-0 text-[#EAB308]" />
      <span className="text-center">
        Scheduled maintenance starts in{" "}
        <b className="text-white tabular-nums" data-testid="scheduled-maintenance-countdown">{countdown}</b>
        {" · "}
        <span className="text-[#FDE68A]/80">{local}{endLocal ? ` → ${endLocal}` : ""}</span>
      </span>
    </div>
  );
}
